import api from '@/services/api'
import { mapDealFromApi } from '@/adapters/deals'
import { formatRelative } from '@/utils/format-date'
import type { Deal, DealReuniao } from '@/types'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isValidReuniaoId(id: unknown): id is string {
  return typeof id === 'string' && UUID_RE.test(id)
}

/** Resposta da API pode vir envelopada em `{ data }` ou como entidade — o campo `data` da reunião é a data/hora. */
export function extractReuniaoFromApiResponse(payload: unknown): DealReuniao | null {
  if (!payload || typeof payload !== 'object') return null
  const root = payload as unknown as Record<string, unknown>

  if (isValidReuniaoId(root.id)) {
    return root as unknown as DealReuniao
  }

  const nested = root.data
  if (nested && typeof nested === 'object' && isValidReuniaoId((nested as DealReuniao).id)) {
    return nested as DealReuniao
  }

  return null
}

export function toDatetimeLocalValue(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset()
  const local = new Date(date.getTime() - offset * 60_000)
  return local.toISOString().slice(0, 16)
}

export function dealHasScheduledMeeting(deal: Deal): boolean {
  const reuniao = deal.reunioes?.[0]
  if (!reuniao?.data) return false
  if (reuniao.status && reuniao.status !== 'Agendada') return false
  return !Number.isNaN(new Date(reuniao.data).getTime())
}

/** Proposta em elaboração com reunião ainda agendada fica na coluna Reunião da jornada. */
export function shouldShowDealInReuniaoJourneyColumn(deal: Deal): boolean {
  return deal.etapa === 'proposta_elaboracao' && dealHasScheduledMeeting(deal)
}

export function getMeetingTiming(data?: string) {
  if (!data) return null
  const meetingDate = new Date(data)
  if (Number.isNaN(meetingDate.getTime())) return null
  const diffMs = meetingDate.getTime() - Date.now()
  if (Math.abs(diffMs) < 60_000) return { tone: 'now' as const, label: 'Acontece agora' }
  return {
    tone: diffMs > 0 ? ('future' as const) : ('past' as const),
    label: formatRelative(meetingDate),
  }
}

async function loadReuniaoIdFromDeal(dealId: string): Promise<string | null> {
  const { data } = await api.get(`/deals/${dealId}`)
  const fresh = mapDealFromApi(data.data ?? data)
  const existing = fresh.reunioes?.find((item) => isValidReuniaoId(item.id))
  return existing?.id ?? null
}

export async function ensureReuniao(deal: Deal, reuniao?: DealReuniao): Promise<string> {
  if (isValidReuniaoId(reuniao?.id)) return reuniao.id

  const fromServer = await loadReuniaoIdFromDeal(deal.id)
  if (fromServer) return fromServer

  if (!deal.dataReuniao) {
    throw new Error('missing_meeting')
  }

  const closerId = deal.closerId?.trim()
  if (!isValidReuniaoId(closerId)) {
    throw new Error('missing_closer')
  }

  const { data } = await api.post('/reunioes', {
    dealId: deal.id,
    closerId,
    data: new Date(deal.dataReuniao).toISOString(),
    tipo: reuniao?.tipo ?? 'Descoberta',
    status: reuniao?.status ?? 'Agendada',
  })

  const created = extractReuniaoFromApiResponse(data)
  if (!created?.id) {
    throw new Error('reuniao_create_failed')
  }

  return created.id
}
